import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, TextField, Grid, Card, CardContent, IconButton, MenuItem, Menu, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, FormControl, InputLabel, Select, Chip, List, ListItemButton, ListItem, Divider, Tooltip, Avatar } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import FilterListIcon from '@mui/icons-material/FilterList';
import API_ENDPOINTS from '../config/apiConfig';
import './Hirees.css';
import AdminUserDetailsModal from './AdminUserDetailsModal'; // Add this import
import FixedSnackbar from './FixedSnackbar';
import { useBranch } from '../context/BranchContext';
import { useTranslation } from '../i18n/i18n';
import MultiSelectPopover from './MultiSelectPopover';
import HireDetailsPanel from './HireDetailsPanel';

function People() {
  const { t } = useTranslation();
  const [hires, setHires] = useState([]);
  const [filteredHires, setFilteredHires] = useState([]); // For search functionality
  const [searchQuery, setSearchQuery] = useState(''); // For search input
  const { branches: availableBranches } = useBranch();
  const [branchFilter, setBranchFilter] = useState('');
  const [email, setEmail] = useState('');
  const [fullname, setFullname] = useState('');
  const [position, setPosition] = useState('');
  const [startDate, setStartDate] = useState('');
  // positions list not used in this component (kept in other screens)
  const [editingHire, setEditingHire] = useState(null);
  const [selectedHire, setSelectedHire] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false); // Add this state
  const [userRole, setUserRole] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuHire, setMenuHire] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ fullname: '', position_id: '', branches: [] });
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [positions, setPositions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fetching, setFetching] = useState(true);
  const [openModulesDialog, setOpenModulesDialog] = useState(false);
  const [allModules, setAllModules] = useState([]);
  const [editingModules, setEditingModules] = useState([]);
  const [moduleSearchQuery, setModuleSearchQuery] = useState('');
  const [loadingModules, setLoadingModules] = useState(false);

  const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleUserDeleted = (email) => {
    setHires((prev) => prev.filter((h) => h.email !== email));
    setFilteredHires((prev) => prev.filter((h) => h.email !== email));
    setSelectedHire(null);
    setDetailsModalOpen(false);
    setSnackbar({ open: true, message: t('delete_success'), severity: 'success' });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // One card per row on mobile so the details panel always follows the selected card.
  const cardsPerRow = isMobile ? 1 : 3;
  const detailsRef = useRef(null);

  // Auto-scroll to details panel when hire is selected
  useEffect(() => {
    if (selectedHire && detailsRef.current) {
      setTimeout(() => {
        detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [selectedHire]);

  useEffect(() => {
    const fetchHires = async () => {
      try {
        setFetching(true);
        const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/details`, { credentials: 'include' });
        const data = await response.json();
        setHires(data || []);
        setFilteredHires(data || []); // Initialize filteredHires with all hires
      } catch (err) {
        setHires([]);
        setFilteredHires([]);
      } finally {
        setFetching(false);
      }
    };

    fetchHires();
    // fetch current user role
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/user/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUserRole(data.role);
        }
      } catch (err) {
        // ignore
      }
    };
    fetchUser();
    // fetch positions for edit dialog
    const fetchPositions = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/positions`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setPositions(data);
        }
      } catch (error) {
        console.error('Error fetching positions:', error);
      }
    };
    fetchPositions();
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
  };

  // Apply combined filters (branch + search)
  useEffect(() => {
    let list = hires || [];
    if (branchFilter) {
      list = list.filter(h => (h.branches || []).includes(branchFilter));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (hire) =>
          (hire.fullname || '').toLowerCase().includes(q) ||
          (hire.email || '').toLowerCase().includes(q) ||
          (hire.position || '').toLowerCase().includes(q)
      );
    }
    setFilteredHires(list);
  }, [hires, searchQuery, branchFilter]);

  // Save/Delete handlers for hires are not used in this view (actions use menus)

  const handleSelectHire = (hire) => {
    setSelectedHire(hire);
  };

  const handleUnselectHire = () => setSelectedHire(null);

  // Helper to determine where to show the info bubble (desktop)
  const getBubbleIndex = () => {
    if (!selectedHire) return -1;
    return filteredHires.findIndex((h) => h.email === selectedHire.email);
  };

  // Helper: map branch id -> branch object and compute contrast text color
  const hexToRgb = (hex) => {
    if (!hex) return null;
    let c = hex.replace('#', '').trim();
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    if (c.length !== 6) return null;
    const num = parseInt(c, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  };

  const getContrastColor = (hex) => {
    const rgb = hexToRgb(hex || '');
    if (!rgb) return '#000';
    // Perceived brightness formula
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b);
    return luminance > 186 ? '#000' : '#fff';
  };

  const getBranchInfo = (branchId) => {
    if (!availableBranches) return null;
    return availableBranches.find(b => (b.branch_id || b.code || b._id) === branchId) || null;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('employees_title') || 'Employees'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
            {t('employees_subtitle') || 'View and manage your team members, their assignments, and learning progress.'}
          </Typography>
        </Box>
      </Box>

      {/* Search & Filter Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder={t('search') || 'Search employees...'}
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
          }}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1, maxWidth: 350 }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: 250 }}>
          <InputLabel>{t('filter_by_branch') || 'Branch'}</InputLabel>
          <Select
            value={branchFilter}
            label={t('filter_by_branch') || 'Branch'}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <MenuItem value="">{t('all_branches') || 'All branches'}</MenuItem>
            {availableBranches.map((b) => (
              <MenuItem key={b.branch_id || b.code || b._id} value={b.branch_id || b.code || b._id}>{b.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2}>
        {fetching ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading employees...
              </Typography>
            </Box>
          </Grid>
        ) : filteredHires.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <Avatar sx={{ width: 64, height: 64, mb: 2, bgcolor: 'grey.100', color: 'grey.400', mx: 'auto' }}>
                <PersonIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {t('no_employees_found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No employees match your current filters.
              </Typography>
            </Box>
          </Grid>
        ) : (
          filteredHires.map((hire, idx) => {
          const isSelected = selectedHire?.email === hire.email;
          const bubbleIndex = getBubbleIndex();
          const isLastInRow = (idx + 1) % cardsPerRow === 0;
          const isBubbleRow =
            bubbleIndex !== -1 &&
            (Math.floor(bubbleIndex / cardsPerRow) === Math.floor(idx / cardsPerRow)) &&
            (isLastInRow || idx === filteredHires.length - 1);

          return (
            <React.Fragment key={hire.email}>
              <Grid item xs={12} sm={4} md={4} sx={{ position: 'relative' }}>
                <div className={isSelected ? 'selected-card-wrapper' : ''}>
                  <Card
                    className="user-card"
                    onClick={() =>
                      isSelected ? handleUnselectHire() : handleSelectHire(hire)
                    }
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      boxShadow: isSelected ? '0 0 0 2px' + theme.palette.primary.main + '40' : 'none',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
                      position: 'relative',
                      zIndex: 3,
                      minHeight: 140,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" className="card-title" title={hire.fullname} noWrap sx={{ width: '100%', fontWeight: 600 }}>{hire.fullname}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('email')}: {hire.email}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('position')}: {hire.position}</Typography>
                      {['admin', 'superadmin'].includes(userRole) && (
                        <IconButton
                          size="small"
                          aria-controls={menuAnchorEl ? 'hire-menu' : undefined}
                          aria-haspopup="true"
                          onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setMenuHire(hire); }}
                          sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                      {/* Branch lozenges */}
                      <Box sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(hire.branches || []).map((bid) => {
                          const br = getBranchInfo(bid) || {};
                          const color = (br.config && br.config.color) || br.color || '#e0e0e0';
                          const label = br.name || bid;
                          return (
                            <Chip key={bid} label={label} size="small" sx={{ backgroundColor: color, color: getContrastColor(color), height: 24, fontSize: '0.75rem' }} />
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                  {/* Vertical lines for selected card */}
                  {isSelected && (
                    <>
                      <div className="vertical-line left" />
                      <div className="vertical-line right" />
                    </>
                  )}
                </div>
              </Grid>
              {isBubbleRow && bubbleIndex !== -1 && (
                <Grid item xs={12} sx={{ position: 'relative' }} ref={detailsRef}>
                  <HireDetailsPanel
                    hire={selectedHire}
                    isAdmin={['admin', 'superadmin'].includes(userRole)}
                    onClose={handleUnselectHire}
                    onOpenMenu={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setMenuHire(selectedHire); }}
                    onViewFull={() => setDetailsModalOpen(true)}
                    getBranchInfo={getBranchInfo}
                    getContrastColor={getContrastColor}
                  />
                  {!isMobile && <div className="horizontal-connector" />}
                </Grid>
              )}
            </React.Fragment>
          );
        }))}
      </Grid>
      <Menu
        id="hire-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => { setMenuAnchorEl(null); setMenuHire(null); }}
      >
        <MenuItem
          onClick={() => {
            setEditForm({
              fullname: menuHire?.fullname || '',
              position_id: menuHire?.position_id || positions.find(position => position.display_text === menuHire?.position)?.id || '',
              branches: menuHire?.branches || [],
            });
            setOpenEditDialog(true);
            setMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('edit')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenDeleteDialog(true);
            setMenuAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('delete')} />
        </MenuItem>
      </Menu>

      <Dialog
        open={openDeleteDialog}
        onClose={() => { setOpenDeleteDialog(false); setDeleteReason(''); }}
      >
        <DialogTitle>{t('confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_delete_prompt')} {menuHire?.fullname}?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('reason_optional')}
            type="text"
            fullWidth
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDeleteDialog(false); setDeleteReason(''); }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={async () => {
              if (!menuHire) return;
              try {
                const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/users/${encodeURIComponent(menuHire.email)}/delete`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ confirm: true, reason: deleteReason })
                });
                if (res.ok) {
                  setSnackbar({ open: true, message: t('delete_success'), severity: 'success' });
                  setHires((prev) => prev.filter((h) => h.email !== menuHire.email));
                  setFilteredHires((prev) => prev.filter((h) => h.email !== menuHire.email));
                } else {
                  setSnackbar({ open: true, message: t('delete_failed'), severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: t('delete_failed'), severity: 'error' });
              } finally {
                setOpenDeleteDialog(false);
                setDeleteReason('');
                setMenuHire(null);
              }
            }}
            color="error"
            variant="contained"
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('edit_user')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('name')}
            fullWidth
            value={editForm.fullname}
            onChange={(e) => setEditForm({ ...editForm, fullname: e.target.value })}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('position')}</InputLabel>
            <Select
              value={editForm.position_id}
              onChange={(e) => setEditForm({ ...editForm, position_id: e.target.value })}
              label={t('position')}
            >
              {positions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.display_text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MultiSelectPopover
            label={t('branches')}
            placeholder={t('select_branches')}
            selectedValues={editForm.branches}
            options={availableBranches.map((branch) => ({
              id: branch.branch_id || branch.code || branch._id,
              label: branch.name,
              description: branch.branch_id || branch.code,
            }))}
            onApply={(branches) => setEditForm((current) => ({ ...current, branches }))}
            searchLabel={t('search')}
            emptyLabel={t('no_branches_found')}
            applyLabel={t('apply')}
            cancelLabel={t('cancel')}
            clearLabel={t('clear')}
            closeLabel={t('close')}
          />
          {userRole && (userRole === 'admin' || userRole === 'superadmin') && (
            <Button
              variant="outlined"
              color="secondary"
              sx={{ mt: 2 }}
              onClick={() => {
                const currentVids = Object.keys(menuHire?.learning_modules || {});
                setEditingModules(currentVids);
                setModuleSearchQuery('');
                fetch(API_ENDPOINTS.LEARNING_MODULES_ALL_META, { credentials: 'include' })
                  .then(res => res.json())
                  .then(data => setAllModules(Array.isArray(data) ? data : []))
                  .catch(() => setAllModules([]));
                setOpenModulesDialog(true);
              }}
            >
              {t('manage_learning_modules')}
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
          <Button
            onClick={async () => {
              if (!editForm.fullname.trim()) {
                setSnackbar({ open: true, message: t('name_required'), severity: 'error' });
                return;
              }
              if (!editForm.position_id) {
                setSnackbar({ open: true, message: t('position_required'), severity: 'error' });
                return;
              }
              if (editForm.branches.length === 0) {
                setSnackbar({ open: true, message: t('please_select_at_least_one_branch'), severity: 'error' });
                return;
              }
              const positionLabel = positions.find(position => position.id === editForm.position_id)?.display_text || '';
              setLoadingEdit(true);
              try {
                const res = await fetch(API_ENDPOINTS.UPDATE_USER(menuHire.email), {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify(editForm),
                });
                if (res.ok) {
                  setSnackbar({ open: true, message: t('user_updated'), severity: 'success' });
                  setHires((prev) =>
                    prev.map((h) =>
                      h.email === menuHire.email
                        ? { ...h, fullname: editForm.fullname, position_id: editForm.position_id, position: positionLabel, branches: editForm.branches }
                        : h
                    )
                  );
                  setFilteredHires((prev) =>
                    prev.map((h) =>
                      h.email === menuHire.email
                        ? { ...h, fullname: editForm.fullname, position_id: editForm.position_id, position: positionLabel, branches: editForm.branches }
                        : h
                    )
                  );
                  if (selectedHire?.email === menuHire.email) {
                    setSelectedHire((prev) => ({
                      ...prev,
                      fullname: editForm.fullname,
                      position_id: editForm.position_id,
                      position: positionLabel,
                      branches: editForm.branches,
                    }));
                  }
                  setOpenEditDialog(false);
                } else {
                  const err = await res.json();
                  setSnackbar({ open: true, message: err.message || t('update_failed'), severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: t('update_failed'), severity: 'error' });
              } finally {
                setLoadingEdit(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={loadingEdit}
            startIcon={loadingEdit ? <CircularProgress size={20} /> : null}
          >
            {loadingEdit ? t('please_wait') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Learning Modules Dialog */}
      <Dialog open={openModulesDialog} onClose={() => setOpenModulesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('manage_learning_modules')} — {menuHire?.fullname}</DialogTitle>
        <DialogContent>
          {/* Currently Assigned Section */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {t('currently_assigned')}
          </Typography>
          {editingModules.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('no_modules_available') || 'No modules assigned'}
            </Typography>
          )}
          <List dense>
            {editingModules.map(vid => {
              const mod = allModules.find(m => m.videoid === vid);
              const progress = menuHire?.learning_modules?.[vid];
              const isCompleted = progress?.state === 'taken' && progress?.passed;
              return (
                <ListItem
                  key={vid}
                  secondaryAction={
                    isCompleted ? (
                      <Tooltip title={t('cannot_remove_completed')}>
                        <span>
                          <IconButton size="small" disabled>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" onClick={() => setEditingModules(prev => prev.filter(v => v !== vid))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    primary={mod?.title || vid}
                    secondary={
                      progress ? (
                        progress.state === 'assigned' ? t('assigned') : progress.passed ? t('passed') : t('failed')
                      ) : t('add_modules')
                    }
                  />
                  {isCompleted && <Chip label={t('passed')} size="small" color="success" sx={{ mr: 1 }} />}
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Add Modules Section */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('available_modules')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={t('search_modules')}
            value={moduleSearchQuery}
            onChange={(e) => setModuleSearchQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {(() => {
              const available = allModules
                .filter(m => !editingModules.includes(m.videoid))
                .filter(m => !moduleSearchQuery || (m.title || '').toLowerCase().includes(moduleSearchQuery.toLowerCase()))
                .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
              if (available.length === 0) {
                return (
                  <ListItem>
                    <ListItemText primary={t('no_modules_to_add') || 'No additional modules available'} />
                  </ListItem>
                );
              }
              return available.map(mod => (
                <ListItemButton key={mod.videoid} onClick={() => setEditingModules(prev => [...prev, mod.videoid])}>
                  <ListItemText
                    primary={mod.title}
                    secondary={mod.source === 'custom' ? t('clm_source_custom') : t('clm_source_shared')}
                  />
                  {mod.is_default && <Chip label={t('is_default')} size="small" color="primary" sx={{ mr: 1 }} />}
                </ListItemButton>
              ));
            })()}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModulesDialog(false)}>{t('cancel')}</Button>
          <Button
            onClick={async () => {
              const currentVids = Object.keys(menuHire?.learning_modules || {});
              const toAdd = editingModules.filter(v => !currentVids.includes(v));
              const toRemove = currentVids.filter(v => !editingModules.includes(v));

              if (toAdd.length === 0 && toRemove.length === 0) {
                setOpenModulesDialog(false);
                return;
              }

              setLoadingModules(true);
              try {
                const res = await fetch(API_ENDPOINTS.HIREE_LEARNING_MODULES(menuHire.email), {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ add: toAdd, remove: toRemove }),
                });
                if (res.ok) {
                  const data = await res.json();
                  const updatedModules = data.learning_modules;
                  setHires(prev => prev.map(h =>
                    h.email === menuHire.email ? { ...h, learning_modules: updatedModules } : h
                  ));
                  setFilteredHires(prev => prev.map(h =>
                    h.email === menuHire.email ? { ...h, learning_modules: updatedModules } : h
                  ));
                  setSelectedHire(prev => prev && prev.email === menuHire.email ? { ...prev, learning_modules: updatedModules } : prev);
                  setSnackbar({ open: true, message: t('modules_updated_success'), severity: 'success' });
                } else {
                  const err = await res.json();
                  setSnackbar({ open: true, message: err.message || t('modules_update_error'), severity: 'error' });
                }
              } catch {
                setSnackbar({ open: true, message: t('modules_update_error'), severity: 'error' });
              } finally {
                setLoadingModules(false);
                setOpenModulesDialog(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={loadingModules || editingModules.length === 0}
            startIcon={loadingModules ? <CircularProgress size={20} /> : null}
          >
            {loadingModules ? t('please_wait') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for delete feedback */}
      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={15000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
      />

      {/* Add the modal at the end of the component */}
      <AdminUserDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        userEmail={selectedHire?.email}
        fullname={selectedHire?.fullname}
        position={selectedHire?.position}
        onDeleted={handleUserDeleted}
      />
    </Box>
  );
}

export default People;
